import { CheckIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { Input } from '../../../components/UI';
import { NewsOptIn } from './NewsOptIn.component';

export const ROLES: [string, string][] = [
    ['wallpaper_brand', 'Wallpaper brand'], ['artist', 'Artist'], ['interior_designer', 'Interior designer'], ['retailer', 'Retailer'],
    ['other', 'Other']
];
export const PRODUCTS: [string, string][] = [['wallpaper', 'Wallpaper'], ['infinity_murals', 'Infinity Murals'], ['wrapping_paper', 'Wrapping Paper']];

export interface AboutYouValue {
    company: string;
    website: string;
    phone: string;
    roles: string[];
    products: string[];
    marketing: boolean;
}

export interface AboutYouProps {
    value: AboutYouValue;
    onChange: (v: AboutYouValue) => void;
    rolesError: boolean;
    websiteError?: string | null;
    phoneError?: string | null;
}

const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

// #region Chips
/** Multi-select as large, tappable chips: the whole label toggles, the order you pick is kept. */
const Chips: React.FunctionComponent<{ options: [string, string][]; picked: string[]; onToggle: (v: string) => void; label: string }> = ({ options, picked, onToggle, label }) => (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
        {options.map(([v, text]) => {
            const on = picked.includes(v);
            return (
                <button
                    key={v} type="button" aria-pressed={on} onClick={() => onToggle(v)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${on ? 'border-emerald-600 bg-emerald-600/10 text-emerald-800 dark:text-emerald-200' : 'border-surface-border text-zinc-700 hover:border-zinc-400 dark:text-zinc-300'}`}
                >
                    {on ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.5} /> : null}{text}
                </button>
            );
        })}
    </div>
);

// #region AboutYou
/**
 * A few quick questions for someone booking straight away, so the rep walks in
 * knowing who they're meeting. The role (one tap) and a phone number are required.
 */
export const AboutYou: React.FunctionComponent<AboutYouProps> = ({ value, onChange, rolesError, websiteError, phoneError }): React.ReactElement => (
    <div className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-1.5">
            <span className="text-sm font-medium">What describes you? <span className="font-normal text-zinc-500">Pick any that apply.</span></span>
            <Chips label="Your roles" options={ROLES} picked={value.roles} onToggle={(v) => onChange({ ...value, roles: toggle(value.roles, v) })} />
            {rolesError ? <span role="alert" className="text-xs text-red-600 dark:text-red-400">Pick at least one so we bring the right person.</span> : null}
        </div>
        <div className="flex flex-col gap-y-1.5">
            <span className="text-sm font-medium">Interested in <span className="font-normal text-zinc-500">(optional)</span></span>
            <Chips label="Products of interest" options={PRODUCTS} picked={value.products} onToggle={(v) => onChange({ ...value, products: toggle(value.products, v) })} />
        </div>
        <label className="flex flex-col gap-y-1 text-sm font-medium"><span>Company or studio <span className="font-normal text-zinc-500">(optional)</span></span>
            <Input value={value.company} autoComplete="organization" maxLength={160} placeholder="Your name is fine if you're just starting" onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, company: ev.target.value })} />
        </label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-y-1 text-sm font-medium"><span>Website <span className="font-normal text-zinc-500">(optional)</span></span>
                <Input value={value.website} autoComplete="url" maxLength={500} placeholder="yourbrand.com" invalid={!!websiteError} onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, website: ev.target.value })} />
                {websiteError ? <span role="alert" className="text-xs font-normal text-red-600 dark:text-red-400">{websiteError}</span> : null}
            </label>
            <label className="flex flex-col gap-y-1 text-sm font-medium"><span>Phone</span>
                <Input type="tel" value={value.phone} autoComplete="tel" maxLength={50} placeholder="+1 555 555 0100" invalid={!!phoneError} onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, phone: ev.target.value })} />
                {phoneError ? <span role="alert" className="text-xs font-normal text-red-600 dark:text-red-400">{phoneError}</span> : null}
            </label>
        </div>
        <NewsOptIn checked={value.marketing} onChange={(on) => onChange({ ...value, marketing: on })} />
    </div>
);
